import { ChevronRight, Stamp } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';

import { fetchTimbrado } from '@/api/timbrado';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Spinner } from '@/components/ui/spinner';
import { classifyError } from '@/lib/errors';
import { queryKeys } from '@/lib/pwa/queryConfig';
import { queryPersistMeta } from '@/lib/pwa/queryPersistence';

const TIMBRADO_KEY = queryKeys.timbrado.me;
const TIMBRADO_PAGE = '/app/configuracoes/timbrado';

/**
 * Resumo compacto do papel timbrado em Configurações. Não edita — mostra o que
 * está configurado (ou que não há) e leva à tela dedicada. A edição vive só em
 * TimbradoPage (single-source-of-truth).
 */
export function TimbradoSummaryCard() {
  const navigate = useNavigate();
  const { data, isLoading, isError, error } = useQuery({
    queryKey: TIMBRADO_KEY,
    queryFn: fetchTimbrado,
    meta: queryPersistMeta.nonEmpty,
  });

  const partes = data
    ? [
        data.has_logo && 'Logo',
        data.header_image_url ? 'Cabeçalho em imagem' : data.header && 'Cabeçalho',
        data.footer_image_url ? 'Rodapé em imagem' : data.footer && 'Rodapé',
        data.watermark && 'Marca d’água',
      ].filter(Boolean)
    : [];
  const configurado = partes.length > 0;

  return (
    <Card className="border bg-card">
      <CardHeader>
        <div className="flex items-center gap-2">
          <Stamp className="h-5 w-5 text-primary" />
          <CardTitle className="text-base">Papel timbrado</CardTitle>
        </div>
        <CardDescription>
          Cabeçalho, rodapé, marca d&apos;água e logo aplicados aos documentos gerados
        </CardDescription>
      </CardHeader>
      <CardContent>
        {isLoading ? (
          <div className="flex justify-center py-4">
            <Spinner size={32} label="Carregando timbrado" />
          </div>
        ) : isError ? (
          <Alert variant="destructive">
            <AlertDescription>{classifyError(error).message}</AlertDescription>
          </Alert>
        ) : (
          <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
            <div className="flex items-center gap-3">
              <div className="flex h-12 w-12 shrink-0 items-center justify-center overflow-hidden rounded-md border bg-muted/30">
                {/* O que sai no alto da folha: a faixa em imagem, quando houver; senão, o logo. */}
                {data?.header_image_url || data?.logo_url ? (
                  <img
                    src={data.header_image_url ?? data.logo_url ?? undefined}
                    alt={data.header_image_url ? 'Cabeçalho do timbrado' : 'Logo do timbrado'}
                    className="h-full w-full object-contain"
                  />
                ) : (
                  <Stamp className="h-5 w-5 text-muted-foreground" />
                )}
              </div>
              <div className="space-y-0.5">
                {configurado ? (
                  <>
                    <p className="text-sm font-medium">Configurado</p>
                    <p className="text-xs text-muted-foreground">{partes.join(' · ')}</p>
                  </>
                ) : (
                  <p className="text-sm text-muted-foreground">
                    Você ainda não configurou um papel timbrado.
                  </p>
                )}
              </div>
            </div>
            <Button
              variant="outline"
              className="w-full sm:w-auto"
              onClick={() => navigate(TIMBRADO_PAGE)}
            >
              {configurado ? 'Editar' : 'Criar'}
              <ChevronRight className="ml-2 h-4 w-4" />
            </Button>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
